<template>
    <div class="bg-white rounded shadow">
        <header class="flex items-center h-16 px-4 py-3 border-b sm:px-6 md:py-4">
            <h1 class="text-lg font-normal">
                Create a document
            </h1>
        </header>

        <documents-store @success="onSuccess">
            <base-form
                slot-scope="{
                    sid,
                    is_loading: is_saving,
                    data: document,
                    error,
                    store,
                    forget
                }"
                @submit="store(form)"
            >
                <div class="px-4 pt-6 border-b sm:px-6">
                    <validator-alert :sid="sid" />

                    <form-section>
                        <template slot="title">Basics</template>
                        <template slot="description">
                            What document are you creating?
                        </template>

                        <grid-layout>
                            <grid-layout-item class="md:w-1/2">
                                <form-item
                                    v-if="$route.query.property"
                                    :sid="sid"
                                    :is-required="true"
                                    name="property"
                                >
                                    <template slot="label">Property</template>

                                    <input-property
                                        v-model="form.property"
                                        :uuid="$route.query.property"
                                        :sid="sid"
                                        :disabled="is_saving"
                                        name="property"
                                    />
                                </form-item>

                                <form-item
                                    v-else
                                    :sid="sid"
                                    :is-required="true"
                                    name="tenancy"
                                >
                                    <template slot="label">Tenancy</template>

                                    <input-tenancy
                                        v-model="form.tenancy"
                                        :uuid="$route.query.tenancy"
                                        :sid="sid"
                                        :disabled="is_saving"
                                        name="tenancy"
                                    />
                                </form-item>
                            </grid-layout-item>

                            <grid-layout-item class="md:w-1/2">
                                <form-item
                                    :sid="sid"
                                    :is-required="true"
                                    name="type"
                                >
                                    <template slot="label">Type</template>

                                    <input-select
                                        v-model="form.type"
                                        :sid="sid"
                                        :disabled="is_saving"
                                        :options="type_options"
                                        placeholder="Type..."
                                        name="type"
                                    />
                                </form-item>
                            </grid-layout-item>

                            <grid-layout-item v-if="form.type === 'epc'">
                                <div class="flex mb-6">
                                    <base-svg
                                        name="icon-important-circle"
                                        class="w-5 h-5 mr-3px"
                                    />

                                    <span class="text-xs leading-tight text-grey-darker mt-2px">
                                        Hint: You can download EPC reports for your properties online on the
                                            <a
                                                href="https://www.epcregister.com/reportSearchAddressByPostcode.html"
                                                class="no-underline text-blue"
                                                target="_blank"
                                            >
                                                Ministory of Housing, Communities & Local Government
                                            </a>
                                            website.
                                    </span>
                                </div>
                            </grid-layout-item>

                            <grid-layout-item>
                                <form-item
                                    :sid="sid"
                                    :is-required="true"
                                    name="title"
                                >
                                    <template slot="label">Title</template>

                                    <input-text
                                        v-model="form.title"
                                        :sid="sid"
                                        :disabled="is_saving"
                                        name="title"
                                    />
                                </form-item>
                            </grid-layout-item>

                            <grid-layout-item>
                                <form-item
                                    :sid="sid"
                                    name="description"
                                >
                                    <template slot="label">Description</template>

                                    <input-textarea
                                        v-model="form.description"
                                        :sid="sid"
                                        :disabled="is_saving"
                                        name="description"
                                    />
                                </form-item>
                            </grid-layout-item>
                        </grid-layout>
                    </form-section>
                </div>

                <div class="px-4 pt-6 border-b sm:px-6">
                    <form-section>
                        <template slot="title">Expiry</template>
                        <template slot="description">
                            If the document expires, tell us when and we'll remind you
                            beforehand.
                        </template>

                        <grid-layout>
                            <grid-layout-item class="md:w-1/2">
                                <form-item
                                    :sid="sid"
                                    name="expires_at"
                                >
                                    <template slot="label">Expires</template>

                                    <input-date
                                        v-model="form.expires_at"
                                        :sid="sid"
                                        :disabled="is_saving"
                                        name="expires_at"
                                    />
                                </form-item>
                            </grid-layout-item>
                        </grid-layout>
                    </form-section>
                </div>

                <div class="px-4 pt-6 border-b sm:px-6">
                    <form-section>
                        <template slot="title">Sharing</template>
                        <template slot="description">
                            Share this document with your
                            <base-abbr
                                :title="$route.query.property
                                    ? 'Tenants of the every tenancy of the property'
                                    : 'Tenants of the tenancy'
                                "
                            >
                                tenants
                            </base-abbr>
                            .
                        </template>

                        <grid-layout>
                            <grid-layout-item>
                                <form-item
                                    :sid="sid"
                                    name="is_shared"
                                >
                                    <template slot="label">Share</template>

                                    <label class="flex items-center cursor-pointer">
                                        <input-checkbox
                                            v-model="form.is_shared"
                                            class="mr-3"
                                        />

                                        <div class="leading-tight">Share with my tenant(s)</div>
                                    </label>
                                </form-item>
                            </grid-layout-item>
                        </grid-layout>
                    </form-section>
                </div>

                <div class="px-4 pt-6 border-b sm:px-6">
                    <form-section>
                        <template slot="title">Files</template>
                        <template slot="description">
                            Upload your files/photos.
                        </template>

                        <grid-layout>
                            <grid-layout-item>
                                <form-item
                                    :sid="sid"
                                    :is-required="true"
                                    name="uploads"
                                >
                                    <template slot="label">Files</template>

                                    <input-uploads
                                        v-model="form.uploads"
                                        ref="uploads"
                                        name="uploads"
                                        :max-files="20"
                                        :sid="sid"
                                        :accepted-files="[
                                            'image/jpeg',
                                            'image/png',
                                            'application/pdf'
                                        ]"
                                        class="mr-3"
                                    />
                                </form-item>
                            </grid-layout-item>
                        </grid-layout>
                    </form-section>
                </div>

                <div class="px-4 py-3 text-right bg-grey-bg sm:px-6">
                    <base-button
                        :is-loading="is_saving"
                        class="w-full btn-blue md:w-auto"
                    >
                        Create Document
                    </base-button>
                </div>

                <error-modal
                    v-if="error && $error.is(error, { exclude: [422] })"
                    :error="error"
                />

                <modal-success
                    :is-open="show_created_modal"
                    @close="show_created_modal = false; forget(); reset()"
                >
                    <template slot="heading">Document created!</template>

                    <p>Your document has been created.</p>

                    <div
                        slot="footer"
                        slot-scope="{ close }"
                        class="flex flex-col md:flex-row-reverse"
                    >
                        <router-link
                            :to="{
                                name: $router.fix('dashboard.*.documents.show'),
                                params: {
                                    document: document.uuid
                                }
                            }"
                            class="text-center btn btn-green"
                        >
                            Go to Document
                        </router-link>

                        <button
                            type="button"
                            @click.prevent="close"
                            class="mt-3 text-center btn md:mt-0 md:mr-3"
                        >
                            Add another
                        </button>
                    </div>
                </modal-success>
            </base-form>
        </documents-store>
    </div>
</template>

<script>
    import _ from 'lodash'
    import DocumentsStore from '../../../../components/documents-store'
    import InputProperty from '../../../../components/input-property'
    import InputTenancy from '../../../../components/input-tenancy'

    export default {
        components: {
            DocumentsStore,
            InputProperty,
            InputTenancy
        },
        data () {
            return {
                show_created_modal: false,
                form: {
                    property: null,
                    tenancy: null,
                    type: '',
                    title: '',
                    description: '',
                    expires_at: null,
                    is_shared: false,
                    uploads: []
                }
            }
        },
        computed: {
            type_options () {
                return _.map([
                    'gas', 'legionaries', 'electrical', 'contract',
                    'epc', 'insurance', 'right_to_rent', 'other'
                ], (value) => {
                    return {
                        value,
                        label: this.$t(`document.type.${value}`)
                    }
                })
            }
        },
        methods: {
            onSuccess () {
                setTimeout(() => {
                    this.show_created_modal = true
                }, 0)
            },
            reset () {
                this.$validator.reset(this.sid)

                this.$refs.uploads.reset()

                this.form.type = ''
                this.form.title = ''
                this.form.description = ''
                this.form.expires_at = null
                this.form.is_shared = false
            }
        }
    }
</script>
